import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PatientFileDto } from 'common/api';
import { Services } from 'services';
import { Store } from 'store';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Box, Button, Image, Spinner, Text } from '@clariness/clarikit-react';
import { DownloadIcon } from '@clariness/clarikit-icons';
import { Events } from '@clariness/tracking-event-runtime';
import genericErrorSrc from 'assets/images/generic-error.png';

const DOWNLOAD_ALL_ID = 'all';
const FILE_NAME_MAX_LENGTH = 24;

function formatEventDateTime(date = new Date()): string {
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

function medicalRecordsArchiveFileName(
  patientInternalId: number | string | undefined
): string {
  return `${patientInternalId ?? ''} - medical records.zip`;
}

type Props = {
  referralId?: string;
  patientInternalId?: number;
  isExternalReferral?: boolean;
};

function formatUploadDate(uploadedAt: string): string {
  const date = new Date(uploadedAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getUTCFullYear()}`;
}

function truncateFileName(fileName: string): string {
  return fileName.length > FILE_NAME_MAX_LENGTH
    ? `${fileName.slice(0, FILE_NAME_MAX_LENGTH)}...`
    : fileName;
}

export const MedicalRecords = ({
  referralId = '',
  patientInternalId,
  isExternalReferral,
}: Props) => {
  const intl = useIntl();
  const { user } = Store.useUser();
  const { showNotification } = Store.useNotifications();
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(
    () => new Set()
  );

  const { files, isLoading, isError, refetch } =
    Services.PatientFiles.useGetPatientFiles(referralId, {
      enabled: !isExternalReferral,
    });

  const runDownload = async (
    id: string,
    fileName: string,
    download: () => Promise<void>
  ) => {
    setDownloadingIds((ids) => new Set(ids).add(id));
    try {
      await download();
      captureEventV2(
        Events.InvestigatorService.InvestigatorDownloadedMedicalRecord({
          UserId: user?.id ?? '',
          ReferralID: referralId,
          DateTime: formatEventDateTime(),
          FileName: fileName,
        })
      );
    } catch {
      showNotification({
        variant: 'error',
        description: intl.formatMessage({
          id: 'is.patient_details_modal.medical_records.download_error',
          defaultMessage: 'Could not download the document. Please try again.',
        }),
      });
    } finally {
      setDownloadingIds((ids) => {
        const next = new Set(ids);
        next.delete(id);
        return next;
      });
    }
  };

  if (isExternalReferral) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        data-testid="medical-records-loader"
        display="flex"
        justifyContent="center"
        style={{ marginTop: '40px' }}
      >
        <Spinner size="medium" colorVariant="green" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="28px"
        style={{ marginTop: '40px' }}
      >
        <Image
          width="213px"
          src={genericErrorSrc}
          alt="Something went wrong illustration"
        />
        <Text as="p" variant="bodyLg" color="color-gray-variant-2">
          <FormattedMessage
            id="is.patient_details_modal.medical_records.load_error"
            defaultMessage="Something went wrong while we tried to display the medical records."
          />
        </Text>
        <Button onClick={() => void refetch()}>
          {intl.formatMessage({
            id: 'is.generic_error.try_again_button',
            defaultMessage: 'Try again',
          })}
        </Button>
      </Box>
    );
  }

  if (!files?.length) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="8"
      width="100%"
      style={{ marginTop: '40px', marginBottom: '40px' }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap="8"
        width="100%"
      >
        <Text as="h6" variant="headingXs">
          {intl.formatMessage({
            id: 'is.patient_details_modal.medical_records.title',
            defaultMessage: 'Medical Records',
          })}
        </Text>
        <Button
          variant="tertiary"
          size="s"
          iconLeft={DownloadIcon}
          loading={downloadingIds.has(DOWNLOAD_ALL_ID)}
          onClick={() =>
            void runDownload(
              DOWNLOAD_ALL_ID,
              medicalRecordsArchiveFileName(patientInternalId),
              () =>
                Services.PatientFiles.downloadPatientFilesArchive(
                  referralId,
                  patientInternalId ?? ''
                )
            )
          }
        >
          {intl.formatMessage({
            id: 'is.patient_details_modal.medical_records.download_all',
            defaultMessage: 'Download All',
          })}
        </Button>
      </Box>
      <Box width="100%" height="1px" backgroundColor="color-gray-variant-4" />
      <Text as="p" variant="bodySm" color="color-gray-variant-2">
        <FormattedMessage
          id="is.patient_details_modal.medical_records.expiry_notice"
          defaultMessage="Documents will expire and no longer be visible after a certain time."
        />
      </Text>
      <Box
        display="flex"
        flexDirection="column"
        gap="16"
        style={{ marginTop: '8px' }}
      >
        {files.map((file: PatientFileDto) => (
          <Box
            key={file.id}
            data-testid="medical-record-row"
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gap="16"
            minWidth="0"
          >
            <Text as="span" variant="bodyMd">
              {formatUploadDate(file.uploadedAt)}
            </Text>
            <Box
              title={file.fileName}
              minWidth="0"
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {truncateFileName(file.fileName)}
            </Box>
            <Button
              variant="tertiary"
              size="s"
              iconLeft={DownloadIcon}
              loading={downloadingIds.has(file.id)}
              accessibilityLabel={intl.formatMessage({
                id: 'is.patient_details_modal.medical_records.download_document',
                defaultMessage: 'Download document',
              })}
              onClick={() =>
                void runDownload(file.id, file.fileName, () =>
                  Services.PatientFiles.downloadPatientFile(referralId, file)
                )
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
